/**
 * EventDialog — CRUD eventi calendario con categorie + ricorrenze.
 *
 * - Categorie preset: evento, chiusura, promemoria, staff, evento_esclusivo
 *   (con colori suggeriti; utente può sovrascrivere).
 * - Ricorrenze: daily/weekly/monthly/yearly/custom con interval + until|count
 *   + byweekday (weekly/custom).
 * - Su edit/delete di evento parte di una serie, chiede scope one|series.
 */
import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Trash2, Save, Sparkles, Repeat } from "lucide-react";

export const CATEGORIES = [
  { id: "evento",             label: "Evento",           color: "#a26a3d" },
  { id: "chiusura",           label: "Chiusura",         color: "#c24b4b" },
  { id: "promemoria",         label: "Promemoria",       color: "#d19a3f" },
  { id: "staff",              label: "Staff",            color: "#3d6a8a" },
  { id: "evento_esclusivo",   label: "Evento esclusivo", color: "#6a3d8a" },
];

const CATEGORY_MAP = Object.fromEntries(CATEGORIES.map((c) => [c.id, c]));

const WEEKDAYS = [
  { i: 0, l: "L" }, { i: 1, l: "M" }, { i: 2, l: "M" }, { i: 3, l: "G" },
  { i: 4, l: "V" }, { i: 5, l: "S" }, { i: 6, l: "D" },
];

const FREQS = [
  { id: "daily",   label: "Ogni giorno" },
  { id: "weekly",  label: "Ogni settimana" },
  { id: "monthly", label: "Ogni mese" },
  { id: "yearly",  label: "Ogni anno" },
  { id: "custom",  label: "Personalizzata" },
];


export default function EventDialog({ event, defaultDate, onClose, onChanged }) {
  const isNew = !event?.id;
  const isSeries = !!event?.series_id;
  const [saving, setSaving] = useState(false);
  const [scope, setScope] = useState("one");    // usato per edit/delete di serie
  const [confirmingSeries, setConfirmingSeries] = useState(null);   // "edit" | "delete" | null

  const [form, setForm] = useState({
    title: event?.title || "",
    date: event?.date || defaultDate || new Date().toISOString().slice(0, 10),
    all_day: event ? !!event.all_day : true,
    time_start: event?.time_start || "20:00",
    category: event?.category || "evento",
    color: event?.color || CATEGORY_MAP["evento"].color,
    notes: event?.notes || "",
    visibility: event?.visibility || "all",
  });

  const [recurrent, setRecurrent] = useState(false);
  const [rec, setRec] = useState({
    freq: "weekly", interval: 1, byweekday: [],
    endMode: "until", until: "", count: 10,
  });

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setR = (k) => (v) => setRec((r) => ({ ...r, [k]: v }));

  const pickCategory = (cid) => {
    setForm((f) => ({ ...f, category: cid,
      // Se l'utente aveva il colore di default della cat precedente, aggiorna.
      color: CATEGORY_MAP[cid].color }));
  };

  const buildRecurrence = () => {
    if (!recurrent || !isNew) return null;
    const rule = { freq: rec.freq, interval: Math.max(1, Number(rec.interval) || 1) };
    if ((rec.freq === "weekly" || rec.freq === "custom") && rec.byweekday.length) {
      rule.byweekday = rec.byweekday;
    }
    if (rec.endMode === "until" && rec.until) rule.until = rec.until;
    if (rec.endMode === "count" && rec.count) rule.count = Math.max(1, Number(rec.count));
    return rule;
  };

  const doSave = async (targetScope = "one") => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.all_day) payload.time_start = null;
      if (isNew) {
        const rule = buildRecurrence();
        if (rule) payload.recurrence = rule;
        const res = await api.post("/admin/calendar-events", payload);
        toast.success(res.data?.occurrences_created > 1
          ? `Serie creata: ${res.data.occurrences_created} occorrenze`
          : "Evento creato");
      } else {
        const url = `/admin/calendar-events/${event.id}?scope=${targetScope}`;
        await api.patch(url, payload);
        toast.success(targetScope === "series"
          ? "Serie aggiornata"
          : "Occorrenza aggiornata");
      }
      onChanged && onChanged();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); setConfirmingSeries(null); }
  };

  const save = () => {
    if (!isNew && isSeries) { setConfirmingSeries("edit"); return; }
    doSave("one");
  };

  const doDelete = async (targetScope = "one") => {
    try {
      await api.delete(`/admin/calendar-events/${event.id}?scope=${targetScope}`);
      toast.success(targetScope === "series" ? "Serie eliminata" : "Evento eliminato");
      onChanged && onChanged();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setConfirmingSeries(null); }
  };

  const del = () => {
    if (!event?.id) return;
    if (isSeries) { setConfirmingSeries("delete"); return; }
    if (!window.confirm(`Eliminare "${event.title}"?`)) return;
    doDelete("one");
  };

  const toggleWD = (i) => {
    setRec((r) => ({
      ...r,
      byweekday: r.byweekday.includes(i)
        ? r.byweekday.filter((x) => x !== i)
        : [...r.byweekday, i],
    }));
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="event-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" style={{ color: form.color }} />
            {isNew ? "Nuovo evento" : (isSeries ? "Modifica evento (serie)" : "Modifica evento")}
          </DialogTitle>
          <DialogDescription>
            Note e eventi separati dalle prenotazioni.
            {isSeries && <span className="text-bronze-700"> · Parte di una serie ricorrente</span>}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-ink-500">Titolo*</Label>
            <Input value={form.title} onChange={(e) => set("title")(e.target.value)}
                    placeholder="Es. Festa KeSpettacolo, Chiuso, Riunione staff"
                    data-testid="event-title" />
          </div>

          {/* Categoria */}
          <div>
            <Label className="text-xs text-ink-500">Categoria</Label>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="event-categories">
              {CATEGORIES.map((c) => (
                <button key={c.id} type="button"
                          onClick={() => pickCategory(c.id)}
                          data-testid={`event-cat-${c.id}`}
                          className={`text-xs rounded-full border px-3 py-1 transition-all
                            ${form.category === c.id ? "ring-2 ring-offset-1" : "opacity-70 hover:opacity-100"}`}
                          style={{
                            borderColor: c.color,
                            color: form.category === c.id ? "white" : c.color,
                            background: form.category === c.id ? c.color : `${c.color}18`,
                            ...(form.category === c.id ? { "--tw-ring-color": c.color } : {}),
                          }}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Colore custom (opzionale sovrascrive quello di categoria) */}
          <div>
            <Label className="text-xs text-ink-500">Colore</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="color" value={form.color}
                      onChange={(e) => set("color")(e.target.value)}
                      className="w-10 h-8 rounded border border-sand-300 cursor-pointer"
                      data-testid="event-color-picker" />
              <span className="text-xs font-mono tabular-nums text-ink-500">{form.color}</span>
              <button type="button"
                        onClick={() => set("color")(CATEGORY_MAP[form.category].color)}
                        className="ml-auto text-[10px] text-ink-500 hover:text-bronze-700 underline"
                        data-testid="event-color-reset">
                Reset al colore categoria
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-ink-500">Data*</Label>
              <Input type="date" value={form.date}
                      onChange={(e) => set("date")(e.target.value)}
                      className="font-sans tabular-nums"
                      data-testid="event-date" />
            </div>
            <div>
              <Label className="text-xs text-ink-500">
                <label className="inline-flex items-center gap-1">
                  <input type="checkbox" checked={form.all_day}
                          onChange={(e) => set("all_day")(e.target.checked)}
                          data-testid="event-allday" />
                  All-day
                </label>
              </Label>
              {!form.all_day && (
                <Input type="time" value={form.time_start}
                        onChange={(e) => set("time_start")(e.target.value)}
                        className="font-sans tabular-nums mt-1"
                        data-testid="event-time" />
              )}
            </div>
          </div>

          {/* Ricorrenza — solo su NEW (edit ricorrenza fatto sulla singola occ o serie via scope) */}
          {isNew && (
            <div className="rounded-md border border-sand-300 bg-sand-50/50 p-3">
              <label className="text-sm font-medium flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={recurrent}
                        onChange={(e) => setRecurrent(e.target.checked)}
                        data-testid="event-recurrent" />
                <Repeat className="w-4 h-4" /> Evento ricorrente
              </label>
              {recurrent && (
                <div className="mt-3 space-y-3" data-testid="event-recurrence-box">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-ink-500">Frequenza</Label>
                      <select value={rec.freq} onChange={(e) => setR("freq")(e.target.value)}
                                data-testid="event-freq"
                                className="w-full rounded-md border border-sand-300 bg-white px-2 py-1.5 text-sm">
                        {FREQS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs text-ink-500">Ogni</Label>
                      <Input type="number" min={1} value={rec.interval}
                              onChange={(e) => setR("interval")(e.target.value)}
                              className="font-sans tabular-nums"
                              data-testid="event-interval" />
                    </div>
                  </div>

                  {(rec.freq === "weekly" || rec.freq === "custom") && (
                    <div>
                      <Label className="text-xs text-ink-500">Giorni della settimana</Label>
                      <div className="flex gap-1 mt-1" data-testid="event-byweekday">
                        {WEEKDAYS.map((w) => (
                          <button key={w.i} type="button"
                                    onClick={() => toggleWD(w.i)}
                                    data-testid={`event-wd-${w.i}`}
                                    className={`w-8 h-8 rounded-full text-xs font-semibold border
                                      ${rec.byweekday.includes(w.i)
                                        ? "bg-bronze-600 text-white border-bronze-600"
                                        : "border-sand-300 text-ink-600 hover:bg-sand-100"}`}>
                            {w.l}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2 items-end">
                    <div>
                      <Label className="text-xs text-ink-500">Fine</Label>
                      <select value={rec.endMode} onChange={(e) => setR("endMode")(e.target.value)}
                                data-testid="event-endmode"
                                className="w-full rounded-md border border-sand-300 bg-white px-2 py-1.5 text-sm">
                        <option value="until">Fino a data</option>
                        <option value="count">Numero occorrenze</option>
                      </select>
                    </div>
                    {rec.endMode === "until" ? (
                      <Input type="date" value={rec.until}
                              onChange={(e) => setR("until")(e.target.value)}
                              className="font-sans tabular-nums"
                              data-testid="event-until" />
                    ) : (
                      <Input type="number" min={1} max={500} value={rec.count}
                              onChange={(e) => setR("count")(e.target.value)}
                              className="font-sans tabular-nums"
                              data-testid="event-count" />
                    )}
                  </div>
                  <div className="text-[10px] text-ink-400">
                    Limite di sicurezza: 500 occorrenze / 2 anni.
                  </div>
                </div>
              )}
            </div>
          )}

          <div>
            <Label className="text-xs text-ink-500">Visibilità</Label>
            <select value={form.visibility}
                    onChange={(e) => set("visibility")(e.target.value)}
                    className="w-full rounded-md border border-sand-300 bg-white px-2 py-1.5 text-sm"
                    data-testid="event-visibility">
              <option value="all">Tutti (Admin + Staff)</option>
              <option value="staff">Solo Staff</option>
              <option value="admin">Solo Admin</option>
            </select>
          </div>

          <div>
            <Label className="text-xs text-ink-500">Note</Label>
            <Textarea value={form.notes}
                        onChange={(e) => set("notes")(e.target.value)}
                        rows={2} data-testid="event-notes" />
          </div>
        </div>

        <DialogFooter className="flex items-center justify-between gap-2">
          {!isNew ? (
            <Button variant="outline" size="sm" onClick={del}
                      className="text-rose-800 border-rose-300 hover:bg-rose-50"
                      data-testid="event-delete">
              <Trash2 className="w-4 h-4 mr-1" /> Elimina
            </Button>
          ) : <div />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Annulla</Button>
            <Button onClick={save} disabled={saving || !form.title.trim()}
                      data-testid="event-save">
              <Save className="w-4 h-4 mr-1" />
              {isNew ? "Crea" : "Salva"}
            </Button>
          </div>
        </DialogFooter>

        {/* Series scope confirmation overlay */}
        {confirmingSeries && (
          <div className="absolute inset-0 bg-white/95 backdrop-blur-sm rounded-2xl flex flex-col items-center justify-center p-6 z-50"
                data-testid="event-scope-dialog">
            <Sparkles className="w-8 h-8 text-bronze-600 mb-2" />
            <div className="text-lg font-semibold mb-1">Serie ricorrente</div>
            <div className="text-sm text-ink-600 text-center mb-4 max-w-xs">
              {confirmingSeries === "edit"
                ? "Vuoi modificare solo questa occorrenza o l'intera serie?"
                : "Vuoi eliminare solo questa occorrenza o l'intera serie?"}
            </div>
            <div className="flex flex-col gap-2 w-full max-w-xs">
              <Button onClick={() => (confirmingSeries === "edit" ? doSave("one") : doDelete("one"))}
                        data-testid="event-scope-one">
                Solo questa occorrenza
              </Button>
              <Button variant="outline"
                        className={confirmingSeries === "delete" ? "text-rose-800 border-rose-300" : ""}
                        onClick={() => (confirmingSeries === "edit" ? doSave("series") : doDelete("series"))}
                        data-testid="event-scope-series">
                Tutta la serie
              </Button>
              <Button variant="ghost" onClick={() => setConfirmingSeries(null)}
                        data-testid="event-scope-cancel">
                Annulla
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
